/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
*/
import {ColumnState} from 'ag-grid-community';
import {createSelector} from 'reselect';
import {createDeepEqualSelector} from '@ion/applications/src/store/selectors/SelectorUtils';
import {DownloadJob, DownloadJobsFilters} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {ApplicationState} from '@/types/redux';

// Download Jobs Reselectors
const selectDownloadJobs = ({downloadjobs}: ApplicationState) => downloadjobs;
const selectDownloadJobsShowDownloadJobs = state => selectDownloadJobs(state).showDownloadJobs;
const selectDownloadJobsDownloadJobs     = state => selectDownloadJobs(state).downloadJobs;
const selectDownloadJobsShowExportJobs   = state => selectDownloadJobs(state).showExportJobs;
const selectDownloadJobsExportJobs       = state => selectDownloadJobs(state).exportJobs;

export const downloadJobsShowDownloadJobsSelector = createSelector(
  selectDownloadJobsShowDownloadJobs, (showDownloadJobs: boolean) => showDownloadJobs
);
export const downloadJobsDownloadJobsSelector = createDeepEqualSelector(
  selectDownloadJobsDownloadJobs, (downloadJobs: DownloadJob) => downloadJobs
);
export const downloadJobsShowExportJobsSelector = createSelector(
  selectDownloadJobsShowExportJobs, (showExportJobs: boolean) => showExportJobs
);
export const downloadJobsExportJobsSelector = createDeepEqualSelector(
  selectDownloadJobsExportJobs, (exportJobs: DownloadJob) => exportJobs
);

// Download Jobs Grid Reselectors
const selectDownloadJobsGridColumnState = ({downloadjobs: {grid}}: ApplicationState) => grid?.columnState;

export const downloadJobsGridColumnStateSelector = createDeepEqualSelector(
  selectDownloadJobsGridColumnState, (columnState: ColumnState[]) => columnState
);

// Download Jobs Modular Search Reselectors
const selectDownloadJobsModularSearch = ({downloadjobs: {search}}) => search;

export const downloadJobsModularSearchSelector = createDeepEqualSelector(
  selectDownloadJobsModularSearch, (search: DownloadJobsFilters) => search
);

// Download Jobs Cancel Job Reselectors
const selectDownloadJobsCancel           = ({downloadjobs: {cancel}}: ApplicationState) => cancel;
const selectDownloadJobsCancelResult     = state => selectDownloadJobsCancel(state).result;
const selectDownloadJobsCancelSelected   = state => selectDownloadJobsCancel(state).selected;
const selectDownloadJobsCancelShowDialog = state => selectDownloadJobsCancel(state).isEditing;
const selectDownloadJobsCancelLoading    = state => selectDownloadJobsCancel(state).loading;
const selectDownloadJobsCancelError      = state => selectDownloadJobsCancel(state).error;

export const downloadJobsCancelResultSelector = createDeepEqualSelector(
  selectDownloadJobsCancelResult, (result: DownloadJob) => result
);
export const downloadJobsCancelSelectedSelector = createDeepEqualSelector(
  selectDownloadJobsCancelSelected, (selected: DownloadJob) => selected
);
export const downloadJobsCancelShowDialogSelector = createSelector(
  selectDownloadJobsCancelShowDialog, (isEditing: boolean) => isEditing
);
export const downloadJobsCancelLoadingSelector = createSelector(
  selectDownloadJobsCancelLoading, (loading: boolean) => loading
);
export const downloadJobsCancelErrorSelector = createDeepEqualSelector(
  selectDownloadJobsCancelError, (error: ServiceError) => error
);

// Download Jobs Rerun Job Reselectors
const selectDownloadJobsRerun           = ({downloadjobs: {rerun}}: ApplicationState) => rerun;
const selectDownloadJobsRerunResult     = state => selectDownloadJobsRerun(state).result;
const selectDownloadJobsRerunSelected   = state => selectDownloadJobsRerun(state).selected;
const selectDownloadJobsRerunShowDialog = state => selectDownloadJobsRerun(state).isEditing;
const selectDownloadJobsRerunLoading    = state => selectDownloadJobsRerun(state).loading;
const selectDownloadJobsRerunError      = state => selectDownloadJobsRerun(state).error;

export const downloadJobsRerunResultSelector = createDeepEqualSelector(
  selectDownloadJobsRerunResult, (result: DownloadJob) => result
);
export const downloadJobsRerunSelectedSelector = createDeepEqualSelector(
  selectDownloadJobsRerunSelected, (selected: DownloadJob) => selected
);
export const downloadJobsRerunShowDialogSelector = createSelector(
  selectDownloadJobsRerunShowDialog, (isEditing: boolean) => isEditing
);
export const downloadJobsRerunLoadingSelector = createSelector(
  selectDownloadJobsRerunLoading, (loading: boolean) => loading
);
export const downloadJobsRerunErrorSelector = createDeepEqualSelector(
  selectDownloadJobsRerunError, (error: ServiceError) => error
);

//Download Jobs View Device Dialog Selectors
const selectDeviceView     = ({downloadjobs: {cancelDevice}}: ApplicationState) => cancelDevice;
const selectDeviceResult   = state => selectDeviceView(state).deviceResult;
const selectedDevice       = state => selectDeviceView(state).selected;
const selectDeviceSelected = state => selectDeviceView(state).deviceSelected;
const selectDeviceEditing  = state => selectDeviceView(state).isEditing;
const selectDeviceLoading  = state => selectDeviceView(state).loading;
const selectDeviceSpinner  = state => selectDeviceView(state).isSpinner;
const selectDeviceError    = state => selectDeviceView(state).error;

export const deviceViewResultSelector = createDeepEqualSelector(
  selectDeviceResult, (deviceResult: DownloadJob) => deviceResult
);

export const deviceViewSelector = createDeepEqualSelector(
  selectedDevice, (selected: DownloadJob) => selected
);

export const deviceViewSelectedSelector = createDeepEqualSelector(
  selectDeviceSelected, (deviceSelected: DownloadJob) => deviceSelected
);

export const deviceViewErrorSelector = createDeepEqualSelector(
  selectDeviceError, (error: ServiceError) => error
);

export const deviceViewEditingSelector = createSelector(
  selectDeviceEditing, (isEditing: boolean) => isEditing
);

export const deviceViewLoadingSelector = createSelector(
  selectDeviceLoading, (loading: boolean) => loading
);

export const deviceViewSpinnerSelector = createSelector(
  selectDeviceSpinner, (isSpinner: boolean) => isSpinner
);

const selectAudit            = ({downloadjobs: {audit}}) => audit;
const auditNotification      = state => selectAudit(state).selected;
const showAuditNotification  = state => selectAudit(state).isEditing;
const selectedAuditRecord    = state => selectAudit(state).auditRecord;
const showAuditChangesDialog = state => selectAudit(state).showChangesDialog;

export const downloadJobIsAuditNotifications = createSelector(
  [showAuditNotification], (isEditing) => isEditing
);

export const downloadJobAuditDialogChanges = createSelector(
  [showAuditChangesDialog], (showChangesDialog) => showChangesDialog
);

export const downloadJobAuditNotifications = createDeepEqualSelector(
  [auditNotification], (selected) => selected
);

export const downloadJobIsAuditRecord = createDeepEqualSelector(
  [selectedAuditRecord], (auditRecord) => auditRecord
);
